
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, User, Crown } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export interface ClientForecast {
    id: string;
    name: string;
    email: string;
    avatar_url?: string;
    is_premium: boolean;
    dailyCapacity: number;
    forecastRate: number; // The rate used for calculation (max(actual, 25))
    actualRate: number;   // For display/comparison
}

interface ForecastingCardProps {
    clients: ClientForecast[];
}

export function ForecastingCard({ clients }: ForecastingCardProps) {

    const calculateGrowth = (capacity: number, rate: number, days: number) => {
        const outreach = capacity * days;
        const connections = Math.round(outreach * (rate / 100));
        return connections;
    };

    if (!clients || clients.length === 0) {
        return null;
    }

    return (
        <Card className="border-sidebar-border/60 shadow-sm bg-card/50 backdrop-blur-sm col-span-full overflow-hidden">
            <CardHeader className="pb-4 border-b border-sidebar-border/60">
                <div className="flex items-center gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        Network Growth Forecast
                    </CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                    Projected new connections based on max daily capacity and acceptance rate.
                </p>
            </CardHeader>

            <CardContent className="p-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-muted/50 text-xs uppercase font-semibold text-muted-foreground border-b border-sidebar-border/60">
                            <tr>
                                <th className="px-6 py-3 min-w-[200px]">Account</th>
                                <th className="px-6 py-3 text-center">Daily Limit</th>
                                <th className="px-6 py-3 text-center">Acceptance Rate</th>
                                <th className="px-6 py-3 text-right text-foreground">30 Days</th>
                                <th className="px-6 py-3 text-right text-foreground">3 Months</th>
                                <th className="px-6 py-3 text-right text-foreground">12 Months</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-sidebar-border/60">
                            {clients.map(client => {
                                const growth30d = calculateGrowth(client.dailyCapacity, client.forecastRate, 30);
                                const growth3m = calculateGrowth(client.dailyCapacity, client.forecastRate, 90);
                                const growth12m = calculateGrowth(client.dailyCapacity, client.forecastRate, 365);

                                return (
                                    <tr key={client.id} className="group hover:bg-muted/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <Avatar className="h-8 w-8 border border-border">
                                                    <AvatarImage src={client.avatar_url} />
                                                    <AvatarFallback className="bg-primary/5 text-primary text-xs">
                                                        {client.name.substring(0, 2).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <div className="font-medium text-foreground flex items-center gap-1.5">
                                                        {client.name}
                                                        {client.is_premium && <Crown className="w-3 h-3 text-amber-500 fill-amber-500/20" />}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">{client.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <Badge variant="secondary" className="font-mono font-normal">
                                                {client.dailyCapacity}/day
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex flex-col items-center">
                                                <span className="font-medium text-foreground">{client.forecastRate.toFixed(1)}%</span>
                                                {client.actualRate < 25 && (
                                                    <span className="text-[10px] text-muted-foreground">(Est. Baseline)</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right font-medium text-foreground">
                                            +{growth30d.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-right font-medium text-foreground">
                                            +{growth3m.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-right font-medium text-emerald-600 dark:text-emerald-400">
                                            +{growth12m.toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </CardContent>
        </Card>
    );
}
